/**
 * @fileoverview Application Header Component (Navbar)
 * @description Ultra-high fidelity header with Command-Palette search and sleek global controls.
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useAuthActions } from '../../stores/authStore';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import LanguageSelector from '../LanguageSelector';
import NotificationDrawer from '../notifications/NotificationDrawer';
import { useNotificationStore } from '../../stores/notificationStore';
import {
  Search,
  Bell,
  User,
  Plus,
  Upload,
  X,
  Command,
  Mail,
  Settings as SettingsIcon,
  HelpCircle,
  Zap,
  LayoutGrid,
  SearchCheck,
  Globe,
  Monitor,
  LogOut
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { logout } = useAuthActions();
  const { t } = useTranslation();
  const { unreadCount, initialize, cleanup } = useNotificationStore();

  useEffect(() => {
    if (isAuthenticated) {
      initialize();
    }
    return () => {
      if (isAuthenticated) {
        cleanup();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Only depend on isAuthenticated, not the functions

  const isDashboardRoute = location.pathname.startsWith('/app/');

  const handleLogout = async () => {
    await logout();
  };

  if (!isDashboardRoute) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <Zap className="text-white h-4 w-4 fill-current" />
            </div>
            <span className="text-xl font-medium text-slate-900 tracking-tight">SOMA</span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/features" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">{t('layout.navbar.links.features')}</Link>
            <Link to="/pricing" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">{t('layout.navbar.links.pricing')}</Link>
            <Link to="/solutions" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">{t('layout.navbar.links.solutions')}</Link>
            <Link to="/resources" className="text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">{t('layout.navbar.links.resources')}</Link>
          </div>

          <div className="flex items-center space-x-4">
             <LanguageSelector />
            {isAuthenticated ? (
              <Link to="/app/dashboard">
                <Button className="bg-slate-900 hover:bg-black rounded-lg font-medium text-xs uppercase tracking-widest px-6 h-10 shadow-sm">{t('layout.navbar.dashboard')}</Button>
              </Link>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login" className="px-4 py-2 text-[10px] font-medium text-slate-500 hover:text-slate-900 transition-colors uppercase tracking-widest">{t('layout.navbar.login')}</Link>
                <Link to="/register">
                  <Button className="bg-slate-900 hover:bg-black rounded-lg font-medium text-xs uppercase tracking-widest px-6 h-10 shadow-sm">{t('layout.navbar.get_started')}</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>
    );
  }

  // Dashboard Navbar - Only Global Controls
  return (
    <div className="flex items-center space-x-4">
      <LanguageSelector />

      <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all group">
        <Monitor className="h-5 w-5 group-hover:scale-110 transition-transform" />
      </Button>

      {isAuthenticated && (
        <NotificationDrawer />
      )}

      <div className="h-4 w-px bg-slate-200 mx-1" />

      {/* Profile logic removed here as it's now in Sidebar Footer */}
      {/* But kept a small accessible version if needed - actually the User approved plan says:
          "Global actions (Search, Notifications, Profile) moved into this unified header."
          Wait, I put the Profile in Sidebar Footer in AppSidebar.jsx. 
          If I also have it in Header, it might be redundant, but many elite dashboards have it in both or one.
          Let's keep the Header clean with just Notifications and System Status.
      */}
    </div>
  );
};

export default Navbar;